import * as React from 'react';
import { useIsSsrContext } from '../../../../contexts/IsSsrContext';
import flattenNavLinks from '../flattenNavLinks';
import type { NavItem } from '../../../../types';

export interface FlyoutWrapperProps {
  /**
   * Unique identifier for this flyout instance (used for hover tracking)
   */
  flyoutId: string;

  /**
   * Navigation items to render (used for flat link generation in SSR modes)
   */
  children: NavItem[];

  /**
   * Function that renders the full flyout content when enabled
   */
  renderFullFlyout: () => React.ReactNode;

  /**
   * CSS class name for the SSR flat links container
   */
  ssrContainerClassName?: string;

  /**
   * Whether this flyout is currently in focused/hovered state
   */
  isInFocusedCategory?: boolean;

  /**
   * Callback when flyout is hovered (for mode tracking)
   */
  onHover?: () => void;
}

/**
 * FlyoutWrapper handles progressive rendering of navigation flyouts based on the configured mode.
 *
 * Modes:
 * - 'default': Standard SSR - flat links on server, full menu after hydration
 * - 'lazy-rerender': SSR flat links, keep until hover, then show full menu only when focused
 * - 'no-ssr-flyout': SSR empty placeholder, render full menu only on first hover
 *
 * This wrapper:
 * 1. Decides what to render based on: isSsr, flyoutRenderMode, hover state, and focus state
 * 2. Tracks when this specific flyout has been hovered (for lazy modes)
 * 3. In lazy-rerender mode, shows/hides the full menu based on isInFocusedCategory
 * 4. Calls onHover callback when hover occurs (for parent components to track)
 */
export const FlyoutWrapper: React.FC<FlyoutWrapperProps> = ({
  flyoutId,
  children,
  renderFullFlyout,
  ssrContainerClassName = 'site-header-nav-flyout',
  isInFocusedCategory = false,
  onHover,
}) => {
  const { isSsr, flyoutRenderMode, markFlyoutHovered, isFlyoutHovered } =
    useIsSsrContext();
  const hasBeenHovered = isFlyoutHovered(flyoutId);

  // Track when this flyout enters hover state
  const handleHover = React.useCallback(() => {
    if (!hasBeenHovered) {
      markFlyoutHovered(flyoutId);
      onHover?.();
    }
  }, [hasBeenHovered, flyoutId, markFlyoutHovered, onHover]);

  // Mode 1: Default - Standard SSR behavior (flat links SSR, full menu after hydration)
  if (flyoutRenderMode === 'default') {
    if (isSsr) {
      return <div className={ssrContainerClassName}>{flattenNavLinks(children)}</div>;
    } else {
      return <>{renderFullFlyout()}</>;
    }
  }

  // Mode 2: Lazy Re-render - SSR flat links, keep until hover, then show full menu
  if (flyoutRenderMode === 'lazy-rerender') {
    if (isSsr) {
      // SSR: Render flat links
      return <div className={ssrContainerClassName}>{flattenNavLinks(children)}</div>;
    } else if (!hasBeenHovered) {
      // Client before first hover: Keep flat links hidden (no className so they're invisible), attach hover handler to trigger re-render
      return (
        <div onMouseEnter={handleHover} style={{ display: 'none' }}>
          {flattenNavLinks(children)}
        </div>
      );
    } else {
      // Client after hover: Always render full menu, CSS animations handle show/hide via focused class
      // The parent component will add 'site-header-nav-flyout-focused' class when isInFocusedCategory is true
      return <>{renderFullFlyout()}</>;
    }
  }

  // Mode 3: No SSR Flyout - SSR empty placeholder, render full menu only on hover
  if (flyoutRenderMode === 'no-ssr-flyout') {
    if (isSsr) {
      // SSR: Render empty placeholder (for layout stability)
      return <div className={ssrContainerClassName} style={{ display: 'none' }} />;
    } else if (!hasBeenHovered) {
      // Client before hover: Still empty, but attach hover listener
      return (
        <div
          className={ssrContainerClassName}
          style={{ display: 'none' }}
          onMouseEnter={handleHover}
        />
      );
    } else {
      // Client after hover: Render full menu
      return <>{renderFullFlyout()}</>;
    }
  }

  // Fallback (should never happen, but TypeScript needs it)
  return null;
};
